import { useState, useEffect } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import axios from "axios";
import $ from "jquery";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "slick-carousel";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { API_BASE_URL } from "../config";

// This build/deployment is dedicated to ONE country/domain — change this
// single line per project. It's only sent as a log of "which site the
// login attempt came from"; the ACTUAL account country used for redirect
// decisions always comes back from the API in response.data.data.country
// (see userDataWithCountry below), never from this constant.
// Main project (root westendjewellery.com) -> "AU"
// UK project   (uk.westendjewellery.com)   -> "UK"
// NZ project   (nz.westendjewellery.com)   -> "NZ"
const PROJECT_COUNTRY = "AU";

// Keep this in sync with ProtectedRoute.js / productgroup.js — used so a
// user who logs in on the WRONG domain (e.g. an NZ account logging in on
// the UK site) gets bounced to their correct domain immediately, instead
// of only catching the mismatch later on the ProductGroup page.
const COUNTRY_DOMAIN_MAP = {
  NZ: "nz.westendjewellery.com",
  UK: "uk.westendjewellery.com",
  US: "uk.westendjewellery.com",
  AU: "westendjewellery.com",
};
const DEFAULT_DOMAIN = "westendjewellery.com";

const Login = ({ setUser }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [captchaValue, setCaptchaValue] = useState("");
  const [captchaImage, setCaptchaImage] = useState("");
  const [userInput, setUserInput] = useState("");
  const [location, setLocation] = useState("");
  const navigate = useNavigate();
  const routerLocation = useLocation();

  // Where to send the user after a successful login. If they arrived via a
  // QR scan (ProtectedRoute's guard adds ?redirect=...), send them straight
  // back to that same URL. Otherwise fall back to /products.
  const redirectTarget =
    new URLSearchParams(routerLocation.search).get("redirect") || "/products";

  useEffect(() => {
    $(".slick-sliders").not(".slick-initialized").slick({
      autoplay: true,
      dots: true,
      arrows: true,
      slidesToShow: 1,
      slidesToScroll: 1,
    });
  }, []);

  // Note: this is ONLY used for the "lastLoginLocation" log (city/region).
  // It is NOT used to decide the account's country — that always comes
  // from the API response (data.country), never from geolocation guesses.
  useEffect(() => {
    const fetchLocation = async () => {
      try {
        const res = await fetch("https://ipapi.co/json/");
        const data = await res.json();
        setLocation(`${data.city}, ${data.region}, ${data.country_name}`);
      } catch (err) {
        console.error("Failed to get location", err);
        setLocation("Unknown");
      }
    };
    fetchLocation();
  }, []);

  // FIXED: only remember the email, never the password — a raw password in
  // sessionStorage is readable by any script on the page (XSS risk) and is
  // unnecessary since the auth cookie already keeps the user signed in.
  useEffect(() => {
    const savedEmail = sessionStorage.getItem("rememberedEmail");
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  const generateCaptchaImage = (text) => {
    const canvas = document.createElement("canvas");
    canvas.width = 120;
    canvas.height = 50;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#f9f9f9";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.font = "28px bold Arial";
    ctx.fillStyle = "#333";
    ctx.fillText(text, 20, 35);
    for (let i = 0; i < 3; i++) {
      ctx.beginPath();
      ctx.moveTo(Math.random() * 120, Math.random() * 50);
      ctx.lineTo(Math.random() * 120, Math.random() * 50);
      ctx.strokeStyle = "#ccc";
      ctx.stroke();
    }
    return canvas.toDataURL();
  };

  const generateCaptcha = () => {
    const captcha = Math.floor(1000 + Math.random() * 9000).toString();
    setCaptchaValue(captcha);
    setCaptchaImage(generateCaptchaImage(captcha));
  };

  useEffect(() => {
    generateCaptcha();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (userInput !== captchaValue) {
        alert("Incorrect CAPTCHA.");
        generateCaptcha();
        setIsLoading(false);
        return;
      }

      const loginTime = new Date().toISOString();
      const Country = PROJECT_COUNTRY;

      // withCredentials: REQUIRED so the browser stores/sends the auth
      // cookie set by the API across westendjewellery.com,
      // uk.westendjewellery.com, nz.westendjewellery.com and
      // webadmin.westendjewellery.com. Without this, cross-subdomain login
      // state silently breaks.
      const response = await axios.post(
        `${API_BASE_URL}/Home/CompanyLogin`,
        { email, password, Country, location, loginTime },
        { withCredentials: true }
      );

      // Backend confirmed: response always includes BOTH status:200 and
      // success:true on a real success. Checking both is just belt-and-braces.
      if (response.data && response.data.status === 200 && response.data.success) {
        const userData = response.data.data;

        // The account's REAL country lives in the database and comes back
        // as userData.country (e.g. "AU" here) — this is authoritative and
        // can differ from PROJECT_COUNTRY (which is just "what site did
        // this login attempt happen on"). Always prefer the API's value.
        const userDataWithCountry = {
          ...userData,
          country: userData.country || Country,
        };

        // FIX: wipe everything left over from any previous session BEFORE
        // writing the new user's data. Without this, logging in as a
        // different account on the same browser can still show the
        // previous user's cached cart/orders/profile data.
        const emailToRemember = rememberMe ? email : null;
        localStorage.clear();
        sessionStorage.clear();

        localStorage.setItem("user", JSON.stringify(userDataWithCountry));
        localStorage.setItem("loginTime", new Date().getTime().toString());
        localStorage.setItem("lastLoginTime", loginTime);
        localStorage.setItem("lastLoginLocation", location);

        // localStorage is per-subdomain and NOT visible across nz./uk./root.
        // This cookie IS shared across all *.westendjewellery.com subdomains,
        // so ProtectedRoute on any of them can tell "this browser is logged
        // in, and which country" even before local storage has anything.
        document.cookie = `wej_country=${userDataWithCountry.country}; domain=.westendjewellery.com; path=/; max-age=2592000; secure; samesite=lax`;

        if (emailToRemember) {
          sessionStorage.setItem("rememberedEmail", emailToRemember);
        } else {
          sessionStorage.removeItem("rememberedEmail");
        }

        setUser(userDataWithCountry);

        window.dispatchEvent(new Event("storage"));
        // If this account belongs to a different country's domain than the
        // one they just logged in on (e.g. an NZ account logging in on the
        // UK site), send them to their correct domain right away instead
        // of leaving them on the wrong one until they happen to hit
        // ProductGroup's check.
        const expectedDomain =
          COUNTRY_DOMAIN_MAP[userDataWithCountry.country?.toUpperCase()] || DEFAULT_DOMAIN;
        if (expectedDomain !== window.location.hostname) {
          window.location.href = `https://${expectedDomain}${redirectTarget}`;
          return;
        }

        navigate(redirectTarget);
      } else {
        alert(response.data?.message || "Invalid credentials");
        generateCaptcha();
      }
    } catch (error) {
      console.error("Login failed", error);
      alert("Login failed. Please try again.");
      generateCaptcha();
    } finally {
      setIsLoading(false);
    }
  };
const [showHolidayNotice, setShowHolidayNotice] = useState(true);

useEffect(() => {
  const hideAfter = new Date("2026-09-16T00:00:00"); // stops showing from 16 Sep 2026 onward
  const now = new Date();
  if (now >= hideAfter) {
    setShowHolidayNotice(false);
  }
}, []);
  return (
    <>
      <Header />
      {showHolidayNotice && (
  <div className="holiday_marquee_wrapper">
    <style>{`
      .holiday_marquee_wrapper {
        width: 100%;
        background-color: #7a5c3d;
        color: #fff;
        overflow: hidden;
        white-space: nowrap;
        padding: 8px 0;
      }
      .holiday_marquee_track {
        display: inline-block;
        padding-left: 100%;
        animation: holiday-marquee-scroll 22s linear infinite;
        font-size: 14px;
        font-weight: 500;
        letter-spacing: 0.2px;
      }
      .holiday_marquee_wrapper:hover .holiday_marquee_track {
        animation-play-state: paused;
      }
      @keyframes holiday-marquee-scroll {
        0% { transform: translateX(0); }
        100% { transform: translateX(-100%); }
      }
    `}</style>
    <div className="holiday_marquee_track">
      Please note: on 14th and 15th Sep 2026, our office will remain closed on the auspicious occasion of the Lord Ganesha Festival. All queries will be addressed by 16th Sep, late evening. Thank you.
    </div>
  </div>
)}
      <div id="content" className="site-content" role="main">
        <div className="section-padding">
          <section className="auth_section">
            <div className="auth_container">
              <div className="auth_left">
                <div className="auth_overlay">
                  <h2 className="auth_title">Welcome Back</h2>
                  <p className="auth_subtext">
                    Log in to continue exploring our exclusive client and partner
                    platform, tailored for your needs.
                  </p>
                </div>
              </div>

              <div className="auth_right">
                <form onSubmit={handleLogin} className="auth_form">
                  <div className="auth_switch">
                    <h3 className="auth_client active">Login</h3>
                  </div>

                  <div className="auth_field">
                    <input
                      type="text"
                      className="auth_input"
                      name="username"
                      id="username"
                      placeholder="Your Email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>

                  <div className="auth_field">
                    <input
                      type={showPassword ? "text" : "password"}
                      className="auth_input"
                      name="password"
                      id="password"
                      placeholder="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                    <i
                      className={`fas ${showPassword ? "fa-eye" : "fa-eye-slash"}`}
                      onClick={() => setShowPassword(!showPassword)}
                      style={{
                        position: "absolute",
                        right: "15px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        cursor: "pointer",
                        color: "#7a5c3d",
                      }}
                    ></i>
                  </div>

                  <div className="auth_options">
                    <div className="auth_remember">
                      <input
                        type="checkbox"
                        id="rememberme"
                        checked={rememberMe}
                        onChange={() => setRememberMe(!rememberMe)}
                      />
                      <label htmlFor="rememberme">Remember me</label>
                    </div>
                    <Link to="/forgot-password" className="auth_forgot">
                      Forgot password?
                    </Link>
                  </div>

                  <div className="auth_captcha_section">
                    <label className="auth_captcha_label">Enter CAPTCHA:</label>
                    <div className="auth_captcha_box">
                      {captchaImage && (
                        <img
                          src={captchaImage}
                          alt="captcha"
                          className="auth_captcha_code"
                        />
                      )}
                      <button
                        type="button"
                        className="auth_captcha_refresh"
                        onClick={generateCaptcha}
                      >
                        &#8635;
                      </button>
                    </div>
                    <input
                      type="text"
                      className="auth_input auth_captcha_input"
                      placeholder="Enter CAPTCHA"
                      value={userInput}
                      onChange={(e) => setUserInput(e.target.value.replace(/\D/g, ""))}
                      required
                    />
                  </div>

                  <div className="auth_button">
                    <button
                      type="submit"
                      className="auth_submit"
                      disabled={isLoading}
                    >
                      {isLoading ? "Loading..." : "Login"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </section>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Login;
